import { Link } from "@tanstack/react-router";
import {
  ShieldCheck,
  PiggyBank,
  Landmark,
  Users,
  Briefcase,
  type LucideIcon,
} from "lucide-react";
import type { TrackSummary } from "@/lib/academy.functions";
import { cn } from "@/lib/utils";

const ICONS: Record<string, LucideIcon> = {
  "shield-check": ShieldCheck,
  "piggy-bank": PiggyBank,
  landmark: Landmark,
  users: Users,
  briefcase: Briefcase,
};

const BORDER: Record<string, string> = {
  protection: "border-t-[hsl(214,84%,32%)]",
  retirement: "border-t-bronze-600",
  legacy: "border-t-[hsl(160,55%,28%)]",
  family: "border-t-[hsl(20,55%,45%)]",
  business: "border-t-charcoal-900",
};

export function TrackCard({ track }: { track: TrackSummary }) {
  const Icon = ICONS[track.icon ?? ""] ?? ShieldCheck;
  return (
    <Link
      to="/academy/tracks/$slug"
      params={{ slug: track.slug }}
      className={cn(
        "group flex h-full flex-col border-t-[3px] bg-white p-6",
        "shadow-[0_1px_3px_rgba(168,139,92,0.08)]",
        "transition-all duration-300 hover:-translate-y-1",
        "hover:shadow-[0_18px_40px_-20px_rgba(168,139,92,0.45)]",
        BORDER[track.color] ?? "border-t-bronze-600",
      )}
      style={{ borderRadius: 4 }}
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-navy-800">
        <Icon className="h-6 w-6 text-bronze-400" strokeWidth={1.6} />
      </div>
      <h3 className="mt-5 font-display text-xl text-navy-900">{track.title}</h3>
      {track.description && (
        <p className="mt-2 font-sans text-[14px] leading-relaxed text-charcoal-500">
          {track.description}
        </p>
      )}
      <div className="mt-auto pt-5">
        <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
          {track.lesson_count} lesson{track.lesson_count === 1 ? "" : "s"}
          {track.total_minutes > 0 ? ` · ~${track.total_minutes} min` : ""}
        </p>
        <span className="mt-3 inline-block font-sans text-[13px] font-semibold text-bronze-600 transition-transform group-hover:translate-x-1">
          Begin track →
        </span>
      </div>
    </Link>
  );
}

export default TrackCard;